import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs/operators';

export type BrandLogoSlot = 'login' | 'header' | 'loading';

export interface BrandLogo {
  src: string;
  width?: number;
  height?: number;
  alt: string;
}

export interface LoginBackground {
  src: string;
  enabled: boolean;
  size: 'contain' | 'cover' | '100% 100%';
}

export interface BrandConfig {
  login: BrandLogo;
  header: BrandLogo;
  loading: BrandLogo;
  favicon: { src: string };
  loginBackground: LoginBackground;
}

@Injectable({ providedIn: 'root' })
export class BrandConfigService {
  // Bundled originals remain a fallback even if an operator removes custom assets.
  private readonly defaults: BrandConfig = {
    login: { src: 'assets/media/logos/logo_square.png', width: 101, height: 101, alt: 'Logo' },
    header: { src: 'assets/media/logos/logo_small.png', height: 40, alt: 'Logo' },
    loading: { src: 'assets/media/logos/logo_square.png', width: 90, alt: 'Logo' },
    favicon: { src: 'assets/media/logos/favicon.ico' },
    loginBackground: { src: 'assets/media/logos/logo_aramark_symbol.svg', enabled: true, size: '100% 100%' }
  };
  private config: BrandConfig = this.defaults;
  private readonly http: HttpClient;

  constructor(backend: HttpBackend) {
    // A public static file must not go through token/locale/error interceptors.
    this.http = new HttpClient(backend);
  }

  load(): Promise<void> {
    this.config = this.defaults;
    return this.http.get<any>('assets/branding/brand.json', {
      params: { refresh: Date.now() + '-' + Math.random().toString(36).slice(2) }
    }).pipe(timeout(3000)).toPromise().then(value => {
      const input = this.isObject(value) ? value : {};
      this.config = {
        login: this.normalize(input.login, 'login'),
        header: this.normalize(input.header, 'header'),
        loading: this.normalize(input.loading, 'loading'),
        favicon: this.isObject(input.favicon) && typeof input.favicon.src === 'string' && input.favicon.src.trim()
          ? { src: input.favicon.src.trim() } : { ...this.defaults.favicon },
        loginBackground: this.normalizeBackground(input.loginBackground)
      };
    }).catch(() => {
      // Missing/invalid configuration must never prevent application bootstrap.
      this.config = this.defaults;
    });
  }

  getLogo(slot: BrandLogoSlot): BrandLogo {
    return { ...this.config[slot] };
  }

  getDefaultLogo(slot: BrandLogoSlot): BrandLogo {
    return { ...this.defaults[slot] };
  }

  getFavicon(): string {
    return this.config.favicon.src;
  }

  getDefaultFavicon(): string {
    return this.defaults.favicon.src;
  }

  getLoginBackground(): LoginBackground {
    return { ...this.config.loginBackground };
  }

  getDefaultLoginBackground(): LoginBackground {
    return { ...this.defaults.loginBackground };
  }

  private normalizeBackground(value: any): LoginBackground {
    if (!this.isObject(value) ||
        (value.src !== undefined && (typeof value.src !== 'string' || !value.src.trim())) ||
        (value.enabled !== undefined && typeof value.enabled !== 'boolean') ||
        (value.size !== undefined && ['contain', 'cover', '100% 100%'].indexOf(value.size) === -1)) {
      return this.getDefaultLoginBackground();
    }
    return {
      src: value.src === undefined ? this.defaults.loginBackground.src : value.src.trim(),
      enabled: value.enabled === undefined ? true : value.enabled,
      size: value.size === undefined ? this.defaults.loginBackground.size : value.size
    };
  }

  private normalize(value: any, slot: BrandLogoSlot): BrandLogo {
    if (!this.isObject(value) || typeof value.src !== 'string' || !value.src.trim() ||
        !this.validDimension(value.width) || !this.validDimension(value.height) ||
        (value.alt !== undefined && typeof value.alt !== 'string')) {
      return this.getDefaultLogo(slot);
    }
    return {
      src: value.src.trim(),
      width: value.width,
      height: value.height,
      alt: value.alt === undefined ? this.defaults[slot].alt : value.alt
    };
  }

  private validDimension(value: any): boolean {
    return value === undefined || (typeof value === 'number' && isFinite(value) && value > 0);
  }

  private isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
}
